import {
  applyFicheDueDay,
  DEFAULT_FICHE_DUE_TIME,
  ficheDueTime,
  joinDueTime,
  snapToQuarter,
  splitDueTime,
} from '../../lib/tasks/taskDueTime';

export type HearingPerson =
  | { kind: 'collaborator'; id: string }
  | { kind: 'contact'; id: string };

export type HearingDraft = {
  label: string;
  description: string;
  court: string;
  day: string;
  startTime: string;
  endTime: string;
  notes: string;
  ownerId: string | null;
  people: HearingPerson[];
  courtTouched: boolean;
  eventTypeId: string | null;
};

export type HearingSource = {
  label: string;
  description: string | null;
  court: string | null;
  starts_at: string;
  ends_at: string | null;
  notes: string | null;
  owner_id: string | null;
  event_type_id?: string | null;
};

export type HearingPayload = {
  label: string;
  description: string | null;
  court: string | null;
  starts_at: string;
  ends_at: string | null;
  notes: string | null;
  owner_id: string | null;
  event_type_id: string | null;
};

export type HearingSave =
  | { ok: true; payload: HearingPayload; people: HearingPerson[] }
  | { ok: false; reason: 'label' | 'day' | 'end' };

function localIsoDay(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function minutes(hhmm: string): number {
  const { hour, minute } = splitDueTime(hhmm);
  return hour * 60 + minute;
}

function blank(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

export function emptyHearingDraft(ownerId: string | null): HearingDraft {
  return {
    label: '',
    description: '',
    court: '',
    day: '',
    startTime: DEFAULT_FICHE_DUE_TIME,
    endTime: '',
    notes: '',
    ownerId,
    people: ownerId ? [{ kind: 'collaborator', id: ownerId }] : [],
    courtTouched: false,
    eventTypeId: null,
  };
}

export function hearingDraftFromRecord(hearing: HearingSource, people: HearingPerson[]): HearingDraft {
  const day = localIsoDay(hearing.starts_at);
  const startTime = ficheDueTime(hearing.starts_at);
  let endTime = '';
  if (hearing.ends_at) {
    const endDay = localIsoDay(hearing.ends_at);
    if (endDay === day) endTime = ficheDueTime(hearing.ends_at);
  }
  return {
    label: hearing.label,
    description: hearing.description ?? '',
    court: hearing.court ?? '',
    day,
    startTime,
    endTime,
    notes: hearing.notes ?? '',
    ownerId: hearing.owner_id,
    people,
    courtTouched: true,
    eventTypeId: hearing.event_type_id ?? null,
  };
}

export function applyCourtPrefill(
  draft: HearingDraft,
  juridiction: string | null,
  mode: 'create' | 'edit',
): HearingDraft {
  if (mode === 'edit' || draft.courtTouched) return draft;
  const court = juridiction?.trim() ?? '';
  if (draft.court === court) return draft;
  return { ...draft, court };
}

/**
 * Choisir un type nomme l'audience tant que le libellé est vide ou porte encore
 * le nom d'un type : un libellé saisi à la main n'est jamais écrasé.
 */
export function setHearingType(
  draft: HearingDraft,
  type: { id: string; name: string } | null,
  typeNames: string[],
): HearingDraft {
  const label = draft.label.trim();
  const free = label === '' || typeNames.includes(label);
  return {
    ...draft,
    eventTypeId: type?.id ?? null,
    label: free && type ? type.name : draft.label,
  };
}

export function setHearingCourt(draft: HearingDraft, court: string): HearingDraft {
  return { ...draft, court, courtTouched: true };
}

/** Une heure après le début. Vide si ça passerait minuit. */
export function defaultEndTime(startTime: string): string {
  const { hour, minute } = splitDueTime(startTime);
  if (hour >= 23) return '';
  return joinDueTime(hour + 1, minute);
}

export function shiftEndAfterStart(startTime: string, endTime: string): string {
  if (endTime && minutes(endTime) > minutes(startTime)) return snapToQuarter(endTime);
  return defaultEndTime(startTime);
}

export function setHearingStart(draft: HearingDraft, day: string, time: string): HearingDraft {
  const startTime = snapToQuarter(time || draft.startTime || DEFAULT_FICHE_DUE_TIME);
  if (!day) return { ...draft, day: '', startTime, endTime: '' };
  return {
    ...draft,
    day,
    startTime,
    endTime: shiftEndAfterStart(startTime, draft.endTime),
  };
}

export function setHearingEnd(draft: HearingDraft, time: string): HearingDraft {
  if (!draft.day) return draft;
  return { ...draft, endTime: time };
}

export function toggleHearingPerson(draft: HearingDraft, person: HearingPerson): HearingDraft {
  const has = draft.people.some((item) => item.kind === person.kind && item.id === person.id);
  return {
    ...draft,
    people: has
      ? draft.people.filter((item) => item.kind !== person.kind || item.id !== person.id)
      : [...draft.people, person],
  };
}

export function commitHearingDraft(
  draft: HearingDraft,
  allowed: { collaboratorIds: string[]; contactIds: string[] },
): HearingSave {
  const label = draft.label.trim();
  if (!label) return { ok: false, reason: 'label' };
  if (!draft.day) return { ok: false, reason: 'day' };
  const startsAt = applyFicheDueDay(draft.day, draft.startTime).due_datetime;
  if (!startsAt) return { ok: false, reason: 'day' };

  let endsAt: string | null = null;
  if (draft.endTime) {
    endsAt = applyFicheDueDay(draft.day, draft.endTime).due_datetime;
    if (!endsAt || new Date(endsAt).getTime() <= new Date(startsAt).getTime()) {
      return { ok: false, reason: 'end' };
    }
  }

  const collaborators = new Set(allowed.collaboratorIds);
  const contacts = new Set(allowed.contactIds);
  const seen = new Set<string>();
  const people: HearingPerson[] = [];
  for (const person of draft.people) {
    const known = person.kind === 'collaborator'
      ? collaborators.has(person.id)
      : contacts.has(person.id);
    const key = `${person.kind}:${person.id}`;
    if (!known || seen.has(key)) continue;
    seen.add(key);
    people.push(person);
  }

  return {
    ok: true,
    people,
    payload: {
      label,
      description: blank(draft.description),
      court: blank(draft.court),
      starts_at: startsAt,
      ends_at: endsAt,
      notes: blank(draft.notes),
      owner_id: draft.ownerId && collaborators.has(draft.ownerId) ? draft.ownerId : null,
      event_type_id: draft.eventTypeId,
    },
  };
}
